<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex justify-space-between align-center flex-wrap ga-2">
        <div>
          <h1 class="text-h4">{{ $t('siscomex.title') }}</h1>
          <div class="text-body-2 text-medium-emphasis">{{ $t('siscomex.subtitle') }}</div>
        </div>
        <v-btn variant="text" prepend-icon="mdi-refresh" :loading="loadingStatus" @click="loadStatus">
          {{ $t('siscomex.refresh') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- Configurazione e sessione -->
      <v-col cols="12" md="5">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-key-chain-variant</v-icon>
            {{ $t('siscomex.config.title') }}
          </v-card-title>

          <v-card-text v-if="status">
            <v-alert v-if="!status.configured" type="warning" variant="tonal" class="mb-4">
              {{ $t('siscomex.config.notConfiguredHint') }}
              <pre class="text-caption mt-2 secrets-hint">{{ secretsCommand }}</pre>
            </v-alert>
            <v-alert v-else-if="status.environment === 'prod'" type="info" variant="tonal" density="compact" class="mb-4">
              {{ $t('siscomex.config.prodWarning') }}
            </v-alert>

            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="text-medium-emphasis">{{ $t('siscomex.config.environment') }}</td>
                  <td>
                    <v-chip size="small" label :color="status.environment === 'prod' ? 'error' : 'teal'">
                      {{ status.environment }}
                    </v-chip>
                  </td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">Base URL</td>
                  <td class="text-caption">{{ status.baseUrl }}</td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">Role-Type</td>
                  <td>{{ status.roleType }}</td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">Client-Id</td>
                  <td><code>{{ status.clientIdMasked || '-' }}</code></td>
                </tr>
              </tbody>
            </v-table>

            <v-divider class="my-4" />

            <div class="d-flex align-center mb-2">
              <span class="text-subtitle-1 mr-2">{{ $t('siscomex.session.title') }}</span>
              <v-chip size="small" label :color="status.authenticated ? 'success' : 'grey'">
                {{ status.authenticated ? $t('siscomex.session.active') : $t('siscomex.session.inactive') }}
              </v-chip>
            </div>

            <v-table density="compact">
              <tbody>
                <tr>
                  <td class="text-medium-emphasis">{{ $t('siscomex.session.expiresAt') }}</td>
                  <td>
                    <template v-if="status.tokenExpiresAt">
                      {{ formatDateTime(status.tokenExpiresAt) }}
                      ({{ $t('siscomex.session.minutesLeft', { n: minutesLeft }) }})
                    </template>
                    <template v-else>-</template>
                  </td>
                </tr>
                <tr>
                  <td class="text-medium-emphasis">{{ $t('siscomex.session.lastAuth') }}</td>
                  <td>{{ status.lastAuthAt ? formatDateTime(status.lastAuthAt) : $t('siscomex.session.never') }}</td>
                </tr>
              </tbody>
            </v-table>

            <div class="d-flex flex-wrap ga-2 mt-4">
              <v-btn
                color="primary"
                prepend-icon="mdi-login"
                :loading="authenticating"
                :disabled="!status.configured || waitSeconds > 0"
                @click="authenticate"
              >
                {{ waitSeconds > 0 ? $t('siscomex.session.wait', { n: waitSeconds }) : $t('siscomex.session.authenticate') }}
              </v-btn>
              <v-btn variant="text" prepend-icon="mdi-logout" :disabled="!status.authenticated" @click="discardSession">
                {{ $t('siscomex.session.discard') }}
              </v-btn>
            </div>
            <div class="text-caption text-medium-emphasis mt-2">{{ $t('siscomex.session.rateHint') }}</div>

            <v-alert
              v-if="sessionError"
              type="error"
              variant="tonal"
              density="compact"
              class="mt-4"
              closable
              @click:close="sessionError = ''"
            >
              {{ sessionError }}
            </v-alert>

            <v-expansion-panels v-if="status.tokenClaims" class="mt-4">
              <v-expansion-panel :title="$t('siscomex.session.claims')">
                <v-expansion-panel-text>
                  <pre class="json-view">{{ pretty(status.tokenClaims) }}</pre>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>

          <v-card-text v-else-if="loadingStatus">
            <v-progress-linear indeterminate color="primary" />
          </v-card-text>

          <v-card-text v-else-if="statusError">
            <v-alert type="error" variant="tonal">{{ $t('siscomex.loadError') }}: {{ statusError }}</v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Prove -->
      <v-col cols="12" md="7">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-flask-outline</v-icon>
            {{ $t('siscomex.tests.title') }}
          </v-card-title>

          <v-tabs v-model="tab" density="compact" show-arrows>
            <v-tab value="tabx">{{ $t('siscomex.tests.tabx') }}</v-tab>
            <v-tab value="duimp">DUIMP</v-tab>
            <v-tab value="catp">{{ $t('siscomex.tests.catp') }}</v-tab>
            <v-tab value="custom">{{ $t('siscomex.tests.custom') }}</v-tab>
          </v-tabs>
          <v-divider />

          <v-card-text>
            <v-window v-model="tab">
              <v-window-item value="tabx">
                <p class="text-body-2 mb-4">{{ $t('siscomex.tests.tabxHint') }}</p>
                <v-btn class="mb-4" variant="tonal" :disabled="busy" @click="run('/tabx/api/ext/tabela')">
                  {{ $t('siscomex.tests.listTables') }}
                </v-btn>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="tabx.table"
                      :label="$t('siscomex.tests.tableName')"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6" class="d-flex flex-wrap ga-2 align-center">
                    <v-btn variant="tonal" :disabled="busy || !tabx.table.trim()" @click="runTable('')">
                      {{ $t('siscomex.tests.queryTable') }}
                    </v-btn>
                    <v-btn variant="text" :disabled="busy || !tabx.table.trim()" @click="runTable('/metadado')">
                      {{ $t('siscomex.tests.metadata') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item value="duimp">
                <p class="text-body-2 mb-4">{{ $t('siscomex.tests.duimpHint') }}</p>
                <v-row dense>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="duimp.number"
                      :label="$t('siscomex.tests.duimpNumber')"
                      placeholder="25BR00000000000"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="duimp.version"
                      :label="$t('siscomex.tests.duimpVersion')"
                      type="number"
                      min="1"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <div class="d-flex flex-wrap ga-2 mt-4">
                  <v-btn variant="tonal" :disabled="busy || !duimpNumber" @click="run(`${duimpBase()}/versoes`)">
                    {{ $t('siscomex.tests.versions') }}
                  </v-btn>
                  <v-btn
                    v-for="op in duimpOps"
                    :key="op.key"
                    variant="tonal"
                    :disabled="busy || !duimpNumber || !duimpVersion"
                    @click="runDuimp(op.suffix)"
                  >
                    {{ $t(`siscomex.tests.${op.key}`) }}
                  </v-btn>
                </div>
              </v-window-item>

              <v-window-item value="catp">
                <p class="text-body-2 mb-4">{{ $t('siscomex.tests.catpHint') }}</p>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="catp.cnpjRoot"
                      :label="$t('siscomex.tests.cnpjRoot')"
                      placeholder="00000000"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="catp.ncm" label="NCM" placeholder="00000000" density="compact" hide-details />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="catp.internalCode"
                      :label="$t('siscomex.tests.internalCode')"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="catp.code"
                      :label="$t('siscomex.tests.productCode')"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="catp.denomination"
                      :label="$t('siscomex.tests.denomination')"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-select
                      v-model="catp.situation"
                      :items="situationItems"
                      :label="$t('siscomex.tests.situation')"
                      density="compact"
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-btn class="mt-4" variant="tonal" :disabled="busy || !digits(catp.cnpjRoot)" @click="runCatalog">
                  {{ $t('siscomex.tests.searchProducts') }}
                </v-btn>
              </v-window-item>

              <v-window-item value="custom">
                <p class="text-body-2 mb-4">{{ $t('siscomex.tests.customHint') }}</p>
                <v-text-field
                  v-model="customPath"
                  :label="$t('siscomex.tests.path')"
                  placeholder="/tabx/api/ext/tabela"
                  prefix="GET"
                  density="compact"
                  @keyup.enter="runCustom"
                />
                <v-btn variant="tonal" :disabled="busy || !customPath.trim().startsWith('/')" @click="runCustom">
                  {{ $t('siscomex.tests.send') }}
                </v-btn>
              </v-window-item>
            </v-window>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Risposta -->
    <v-row>
      <v-col cols="12">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center flex-wrap ga-2">
            <v-icon class="mr-2">mdi-code-json</v-icon>
            {{ $t('siscomex.result.title') }}
            <v-spacer />
            <template v-if="result">
              <v-chip size="small" label :color="statusColor(result.statusCode)">{{ result.statusCode ?? '—' }}</v-chip>
              <v-chip size="small" label variant="outlined">{{ result.durationMs }} ms</v-chip>
              <v-chip v-if="result.authenticatedNow" size="small" label color="info">
                {{ $t('siscomex.result.authenticatedNow') }}
              </v-chip>
              <v-btn size="small" variant="text" prepend-icon="mdi-content-copy" :disabled="!resultBody" @click="copyResult">
                {{ copied ? $t('siscomex.result.copied') : $t('siscomex.result.copy') }}
              </v-btn>
            </template>
          </v-card-title>
          <v-progress-linear v-if="busy" indeterminate color="primary" />
          <v-card-text>
            <div v-if="!result && !busy" class="text-medium-emphasis">{{ $t('siscomex.result.empty') }}</div>
            <template v-if="result">
              <div class="text-caption mb-2"><code>{{ result.method }} {{ result.url }}</code></div>
              <v-alert v-if="result.error" type="error" variant="tonal" density="compact" class="mb-2">
                {{ result.error }}
              </v-alert>
              <v-alert v-if="result.truncated" type="warning" variant="tonal" density="compact" class="mb-2">
                {{ $t('siscomex.result.truncated') }}
              </v-alert>
              <v-alert v-if="ignoredChecks.length" type="warning" variant="tonal" density="compact" class="mb-2">
                {{ $t('siscomex.result.ignoredFilters', { filters: ignoredChecks.map(c => c.name).join(', ') }) }}
                <v-switch
                  v-model="applyCatalogFilters"
                  :label="$t('siscomex.result.applyHere')"
                  color="primary"
                  density="compact"
                  hide-details
                />
              </v-alert>
              <div v-if="resultItems" class="d-flex align-center flex-wrap ga-2 mb-2">
                <v-text-field
                  v-model="localFilter"
                  :label="$t('siscomex.result.filter')"
                  prepend-inner-icon="mdi-magnify"
                  density="compact"
                  hide-details
                  clearable
                  class="result-filter"
                />
                <span class="text-caption text-medium-emphasis">
                  {{ $t('siscomex.result.filterCount', { n: filteredItems.length, total: resultItems.length }) }}
                </span>
              </div>
              <pre v-if="resultBody" class="json-view">{{ resultBody }}</pre>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiCall } from '../utils/api.js'

const { t } = useI18n()

const secretsCommand = [
  'dotnet user-secrets set "Siscomex:ClientId" "<client-id>" --project Backend',
  'dotnet user-secrets set "Siscomex:ClientSecret" "<client-secret>" --project Backend'
].join('\n')

const status = ref(null)
const loadingStatus = ref(false)
const statusError = ref('')
const authenticating = ref(false)
const sessionError = ref('')
// Secondi prima che il portale accetti una nuova autenticazione: parte dal valore del Backend e scende qui
const waitSeconds = ref(0)
const now = ref(Date.now())

const tab = ref('tabx')
const busy = ref(false)
const result = ref(null)
const copied = ref(false)
// Filtro testuale sulla risposta, quando è una lista
const localFilter = ref('')
// Riapplica qui i filtri del catalogo che il portale ha ignorato
const applyCatalogFilters = ref(true)

const tabx = ref({ table: '' })
const duimp = ref({ number: '', version: '1' })
const catp = ref({ cnpjRoot: '', ncm: '', code: '', internalCode: '', denomination: '', situation: '' })

const situationItems = computed(() => [
  { title: t('siscomex.tests.situationAll'), value: '' },
  { title: t('siscomex.tests.situationActive'), value: '0' },
  { title: t('siscomex.tests.situationInactive'), value: '1' },
  { title: t('siscomex.tests.situationDraft'), value: '2' }
])
const customPath = ref('/tabx/api/ext/tabela')

const duimpOps = [
  { key: 'general', suffix: '' },
  { key: 'items', suffix: '/itens' },
  { key: 'calculated', suffix: '/valores-calculados' },
  { key: 'diagnostics', suffix: '/diagnosticos' }
]

const enc = (value) => encodeURIComponent(String(value).trim())
const digits = (value) => String(value || '').replace(/\D/g, '')
const pretty = (value) => JSON.stringify(value, null, 2)
const formatDateTime = (value) => new Date(value).toLocaleString()
const errorMessage = (error) => error.response?.data?.message || error.message

const minutesLeft = computed(() => {
  if (!status.value?.tokenExpiresAt) return 0
  return Math.max(0, Math.round((new Date(status.value.tokenExpiresAt).getTime() - now.value) / 60000))
})

const resultItems = computed(() => (Array.isArray(result.value?.json) ? result.value.json : null))

// Confronto esatto fra i filtri chiesti al catalogo e i campi di ProdutoIntegracaoDTO
const catalogChecks = (filters) => [
  filters.ncm && { name: 'ncm', test: (item) => digits(item.ncm) === filters.ncm },
  filters.codigo && { name: 'codigo', test: (item) => Number(item.codigo) === Number(filters.codigo) },
  filters.codigoInterno && {
    name: 'codigoInterno',
    test: (item) => JSON.stringify(item.codigosInterno || []).toLowerCase().includes(filters.codigoInterno.toLowerCase())
  }
].filter(Boolean)

// Filtri che il portale non ha applicato: fra i prodotti restituiti ce n'è almeno uno che non li rispetta
const ignoredChecks = computed(() => {
  const items = resultItems.value
  const filters = result.value?.catalogFilters
  if (!filters || !items?.length) return []
  return catalogChecks(filters).filter((check) => items.some((item) => !check.test(item)))
})

const filteredItems = computed(() => {
  let items = resultItems.value
  if (!items) return null
  if (applyCatalogFilters.value && ignoredChecks.value.length) {
    items = items.filter((item) => ignoredChecks.value.every((check) => check.test(item)))
  }
  const needle = (localFilter.value || '').trim().toLowerCase()
  if (needle) items = items.filter((item) => JSON.stringify(item).toLowerCase().includes(needle))
  return items
})

const resultBody = computed(() => {
  if (!result.value) return ''
  if (filteredItems.value) return pretty(filteredItems.value)
  if (result.value.json != null) return pretty(result.value.json)
  return result.value.text || ''
})

const statusColor = (code) => {
  if (!code) return 'grey'
  if (code < 300) return 'success'
  if (code < 500) return 'warning'
  return 'error'
}

// Ogni risposta del Backend porta lo stato aggiornato della sessione
const applyStatus = (session) => {
  if (!session) return
  status.value = session
  waitSeconds.value = session.secondsUntilAuthAllowed || 0
}

const loadStatus = async () => {
  loadingStatus.value = true
  statusError.value = ''
  try {
    applyStatus(await apiCall('/api/siscomex/status'))
  } catch (error) {
    statusError.value = errorMessage(error)
  } finally {
    loadingStatus.value = false
  }
}

const authenticate = async () => {
  authenticating.value = true
  sessionError.value = ''
  try {
    applyStatus(await apiCall('/api/siscomex/authenticate', { method: 'POST' }))
  } catch (error) {
    sessionError.value = errorMessage(error)
    applyStatus(error.response?.data?.session)
  } finally {
    authenticating.value = false
  }
}

const discardSession = async () => {
  sessionError.value = ''
  try {
    applyStatus(await apiCall('/api/siscomex/session', { method: 'DELETE' }))
  } catch (error) {
    sessionError.value = errorMessage(error)
  }
}

// `extra` si aggiunge al risultato (es. i filtri chiesti al catalogo, per verificarli sulla risposta)
const run = async (path, extra = {}) => {
  busy.value = true
  copied.value = false
  localFilter.value = ''
  applyCatalogFilters.value = true
  try {
    const response = await apiCall('/api/siscomex/request', {
      method: 'POST',
      body: JSON.stringify({ path })
    })
    result.value = { ...response, ...extra }
    applyStatus(response.session)
  } catch (error) {
    // Percorso rifiutato dal Backend (400): nessuna chiamata è partita verso il portale
    result.value = {
      method: 'GET',
      url: path,
      statusCode: error.response?.status,
      durationMs: 0,
      error: errorMessage(error)
    }
  } finally {
    busy.value = false
  }
}

const runTable = (suffix) => run(`/tabx/api/ext/tabela/${enc(tabx.value.table)}${suffix}`)

const duimpNumber = computed(() => duimp.value.number.trim().toUpperCase())
const duimpVersion = computed(() => String(duimp.value.version ?? '').trim())
const duimpBase = () => `/duimp-api/api/ext/duimp/${enc(duimpNumber.value)}`
const runDuimp = (suffix) => run(`${duimpBase()}/${enc(duimpVersion.value)}${suffix}`)

// `codigo` è il codice numerico assegnato dal portale; il codice dell'azienda è `codigoInterno`
const runCatalog = () => {
  const form = catp.value
  const filters = {
    ncm: digits(form.ncm),
    codigo: digits(form.code),
    codigoInterno: form.internalCode.trim()
  }
  const params = new URLSearchParams({ cpfCnpjRaiz: digits(form.cnpjRoot) })
  if (filters.ncm) params.set('ncm', filters.ncm)
  if (filters.codigo) params.set('codigo', filters.codigo)
  if (filters.codigoInterno) params.set('codigoInterno', filters.codigoInterno)
  if (form.denomination.trim()) params.set('denominacao', form.denomination.trim())
  if (form.situation) params.set('situacao', form.situation)
  run(`/catp/api/ext/produto?${params}`, { catalogFilters: filters })
}

const runCustom = () => {
  if (!customPath.value.trim().startsWith('/')) return
  run(customPath.value.trim())
}

const copyResult = async () => {
  try {
    await navigator.clipboard.writeText(resultBody.value)
    copied.value = true
  } catch (error) {
    console.error('Copy failed:', error)
  }
}

let timer = null

onMounted(() => {
  loadStatus()
  timer = setInterval(() => {
    now.value = Date.now()
    if (waitSeconds.value > 0) waitSeconds.value--
  }, 1000)
})

onBeforeUnmount(() => clearInterval(timer))
</script>

<style scoped>
.json-view {
  max-height: 60vh;
  overflow: auto;
  padding: 12px;
  border-radius: 4px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.result-filter {
  max-width: 360px;
}

.secrets-hint {
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
